import { memo, useState } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import { FoldHeaderText } from "./SubComponents/FoldHeaderText";
import ExpandableBox from "../Containers/ExpandableBox";

type FAQFoldProps = {
  isDesktopSize: boolean;
};

type FAQItem = {
  question: string;
  answer: string;
};

const FAQ_ITEMS: FAQItem[] = [
  {
    question:
      "How is Markit different from other creator or social media platforms?",
    answer:
      "Markit is the only texting platform built for creators, brands, and businesses. Instead of fighting an algorithm, you reach your audience directly in the one place they check every day — their texts — and everything from signups to sales happens in one place.",
  },
  {
    question:
      "How is Markit different from other texting and newsletter platforms?",
    answer:
      "Most texting tools are built for blasting promotions, and most newsletter platforms are built for email with texting bolted on. Markit is text-native and built for publishing — newsletters, digital products, paid text lists, and events all run off your centralized Markit phone number.",
  },
  {
    question: "How do I get paid?",
    answer:
      "Connect your bank through Stripe once, and every product, membership, and ticket sale pays out automatically — usually two business days after it clears. Stripe's processing fee comes out of each payout, and on Pro and Business plans Markit takes no cut of your product and membership sales.",
  },
  {
    question: "Can I import my existing text list from another platform?",
    answer:
      "Yes. Upload a CSV, sync your phone's contacts from the mobile app, add people one at a time, or push them in through the API. You just need to confirm you have consent from everyone whose number you're uploading.",
  },
  {
    question: "Can I track the performance of my texts?",
    answer:
      "Every text gets its own campaign dashboard — recipients, opens, link conversions, and the replies that come back. Contacts from Instagram automations are tracked separately, so you can see exactly what your social audience converts to compared to your other sources.",
  },
  {
    question: "What does it cost to get started?",
    answer:
      "Markit is free to start, with 500 texts a month included on the free plan. Markit+ unlocks higher volume, automations, contact imports, and the API, and it's priced by texts sent rather than by how many contacts you have.",
  },
  {
    question: "Do I own my audience?",
    answer:
      "Yes. Your contacts are yours — export the full list whenever you want and take it with you if you ever leave. Markit never messages your audience on its own or shares their information with anyone else.",
  },
  {
    question: "Is texting my audience actually legal?",
    answer:
      "Yes, as long as people opt in — and Markit handles that part for you. Consent language, STOP opt-outs, carrier registration, and quiet hours are all built in by default, so you stay compliant without having to become an expert on it.",
  },
  {
    question: "Do I get my own phone number?",
    answer:
      "Yes. You get your own dedicated number, not a shared shortcode. Your texts arrive as a real contact card, so they read like a message from you — and when someone replies, it comes straight back to you.",
  },
  {
    question: "What if I don't have a big following yet?",
    answer:
      "You don't need one to start. The people who hand over their phone number are the ones who'd actually buy from you, so a small text list is usually worth more than a big follower count. Markit works the same whether you're texting fifty people or fifty thousand.",
  },
];

type FAQRowProps = {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  isLast: boolean;
  isDesktopSize: boolean;
};

const FAQRow = (props: FAQRowProps) => {
  const { item, isOpen, onToggle, isLast, isDesktopSize } = props;

  return (
    <div
      className="ColumnNormal"
      style={{
        borderBottom: isLast ? undefined : `1px solid ${Colors.BLACK1}`,
      }}
    >
      <div
        className="AlignedRowSpaced"
        style={{
          gap: 20,
          paddingBlock: isDesktopSize ? 24 : 20,
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        <span
          style={{
            fontSize: isDesktopSize ? 18 : 16,
            fontWeight: 600,
            color: Colors.WHITE,
          }}
        >
          {item.question}
        </span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 400,
            color: Colors.GRAY2,
            flexShrink: 0,
            lineHeight: "20px",
          }}
        >
          {isOpen ? "-" : "+"}
        </span>
      </div>
      <ExpandableBox>
        {isOpen ? (
          <p
            style={{
              margin: 0,
              paddingBottom: isDesktopSize ? 24 : 20,
              maxWidth: 720,
              fontSize: isDesktopSize ? 15 : 14,
              lineHeight: "22px",
              color: Colors.GRAY2,
            }}
          >
            {item.answer}
          </p>
        ) : null}
      </ExpandableBox>
    </div>
  );
};

const FAQFold = (props: FAQFoldProps) => {
  const { isDesktopSize } = props;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const styles = {
    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },
  };

  return (
    <div
      className="ColumnCentering"
      style={{ gap: isDesktopSize ? 60 : 40, width: "100%" }}
    >
      <div
        className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
        style={{ gap: 24, width: isDesktopSize ? undefined : "100%" }}
      >
        <div
          className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
          style={{ gap: 10 }}
        >
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon
              icon="ri:question-fill"
              height={14}
              style={{ color: Colors.GREEN2 }}
            />
            <span style={{ ...styles.headerLabel, color: Colors.GREEN2 }}>
              FAQ
            </span>
          </div>
          <FoldHeaderText
            isDesktopSize={isDesktopSize}
            textStyles={{ textAlign: isDesktopSize ? "center" : "left" }}
          >
            Frequently asked questions
          </FoldHeaderText>
        </div>
      </div>
      <div
        className="ColumnNormal slide-up"
        style={{ width: "100%", maxWidth: 1152 }}
      >
        {FAQ_ITEMS.map((item, index) => (
          <FAQRow
            key={item.question}
            item={item}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            isLast={index === FAQ_ITEMS.length - 1}
            isDesktopSize={isDesktopSize}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(FAQFold);
